import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, Copy, CheckCheck, UserPlus, AlertCircle, AlertTriangle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateWorkspaceUser,
  type CreateUserInput,
  type CreateUserResult,
  type WorkspaceRole,
} from '@/lib/hooks/workspace-users';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

interface FormValues {
  email: string;
  name: string;
  role: WorkspaceRole;
}

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  WORKSPACE_ADMIN: 'Administrador',
  OPERADOR: 'Operador',
  CONTADOR_EXTERNO: 'Contador externo',
};

export function UserInviteDrawer({ open, onOpenChange, workspaceId }: Props) {
  const [created, setCreated] = useState<CreateUserResult | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { email: '', name: '', role: 'OPERADOR' },
  });

  const roleValue = watch('role');

  const createMut = useCreateWorkspaceUser(workspaceId);

  useEffect(() => {
    if (!open) {
      reset({ email: '', name: '', role: 'OPERADOR' });
      setCreated(null);
      setCopied(false);
    }
  }, [open, reset]);

  async function onSubmit(values: FormValues) {
    const input: CreateUserInput = {
      email: values.email,
      name: values.name,
      role: values.role,
    };
    try {
      const result = await createMut.mutateAsync(input);
      setCreated(result);
      toast.success(`Usuário "${result.user.name}" convidado.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('409') || msg.toLowerCase().includes('conflict') || msg.toLowerCase().includes('already')) {
        toast.error('Este e-mail já está cadastrado neste workspace.');
      } else {
        toast.error('Falha ao convidar usuário. Tente novamente.');
      }
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copiado para a área de transferência.');
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {created ? (
          <div className="flex flex-col flex-1 min-h-0">
            <SheetHeader
              eyebrow="Usuário convidado"
              title={created.user.name}
              description="Guarde a senha temporária agora — não vamos mostrar de novo."
            />
            <SheetBody className="space-y-5">
              <div className="rounded-2 border border-card-stroke bg-card-soft p-4 space-y-3">
                <div>
                  <Eyebrow>E-mail</Eyebrow>
                  <p className="font-mono text-sm text-fg mt-0.5">{created.user.email}</p>
                </div>
                <div>
                  <Eyebrow>Perfil</Eyebrow>
                  <p className="text-sm text-fg mt-0.5">{ROLE_LABELS[created.user.role]}</p>
                </div>
              </div>

              <div className="rounded-3 border border-warn/30 bg-warn-soft/40 p-5">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-warn shrink-0 mt-0.5" strokeWidth={1.8} />
                  <p className="text-sm font-medium text-warn">
                    Senha temporária · só agora
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-[15px] font-semibold text-fg bg-card border border-card-stroke rounded-2 px-3 py-2 select-all break-all">
                    {created.tempPassword}
                  </code>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => copyToClipboard(created.tempPassword)}
                  >
                    {copied ? (
                      <>
                        <CheckCheck className="h-4 w-4" strokeWidth={1.8} />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" strokeWidth={1.8} />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-fg-muted mt-3">
                  O usuário troca essa senha no primeiro login em{' '}
                  <code>/configuracoes</code>.
                </p>
              </div>

              <div className="rounded-2 border border-card-stroke p-4 space-y-2">
                <Eyebrow>Próximos passos</Eyebrow>
                <ul className="text-sm text-fg-muted space-y-1.5">
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    Compartilhe o e-mail e a senha temporária com o usuário
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    O usuário faz login e define uma nova senha
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    Você pode alterar o perfil a qualquer momento nesta tela
                  </li>
                </ul>
              </div>
            </SheetBody>
            <SheetFooter>
              <Button variant="primary" type="button" onClick={() => onOpenChange(false)}>
                Concluir
              </Button>
            </SheetFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <SheetHeader
              eyebrow="Convidar membro"
              title="Novo usuário"
              description="Crie um acesso para alguém colaborar neste workspace."
            />
            <SheetBody className="space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="grid h-9 w-9 place-items-center rounded-2 bg-mint-soft text-mint-deep">
                    <UserPlus className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="invite-name" className="mb-1.5 block">
                  Nome completo
                </Label>
                <Input
                  id="invite-name"
                  placeholder="Ex.: Maria Silva"
                  autoComplete="name"
                  {...register('name', {
                    required: 'Obrigatório',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                    maxLength: { value: 120, message: 'Máximo 120 caracteres' },
                  })}
                />
                {errors.name && (
                  <p className="text-[11px] text-warn mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="invite-email" className="mb-1.5 block">
                  E-mail
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="maria@instituto.org.br"
                  autoComplete="email"
                  {...register('email', {
                    required: 'Obrigatório',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'E-mail inválido',
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-[11px] text-warn mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="invite-role" className="mb-1.5 block">
                  Perfil de acesso
                </Label>
                <Select
                  value={roleValue}
                  onValueChange={(v) => setValue('role', v as WorkspaceRole)}
                >
                  <SelectTrigger id="invite-role">
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WORKSPACE_ADMIN">Administrador</SelectItem>
                    <SelectItem value="OPERADOR">Operador</SelectItem>
                    <SelectItem value="CONTADOR_EXTERNO">Contador externo</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-fg-muted mt-1">
                  A senha temporária aparece na próxima tela. Anote antes de fechar.
                </p>
              </div>

              <div className="rounded-2 border border-card-stroke bg-card-soft p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" strokeWidth={1.6} />
                <p className="text-[12px] text-fg-muted leading-relaxed">
                  <strong className="text-fg font-medium">Administrador</strong> tem acesso
                  completo, incluindo gerenciar outros usuários.{' '}
                  <strong className="text-fg font-medium">Operador</strong> lança e consulta dados.{' '}
                  <strong className="text-fg font-medium">Contador externo</strong> só visualiza.
                </p>
              </div>
            </SheetBody>
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="ghost" type="button">
                  Cancelar
                </Button>
              </SheetClose>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    Convidando…
                  </>
                ) : (
                  'Convidar usuário'
                )}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
